import { useEffect, useRef, useState } from 'react';

/**
 * Minimal IntersectionObserver wrapper for a one-time "has this section
 * entered the viewport yet" flag -- used for the dashboard's restrained
 * fade+rise section entrance. No animation library added for this; it's a
 * single boolean derived from a native browser API. Fires once and
 * disconnects, so scrolling back up/down doesn't re-trigger it.
 */
export const useInView = <T extends HTMLElement>(threshold = 0.15) => {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reduced-motion users get content immediately, not gated behind a
    // scroll-triggered reveal -- this is a visual flourish, not something
    // that should ever delay content becoming visible.
    if (typeof IntersectionObserver === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
};
